import { t } from 'i18next';
import { FiltersState } from 'src/feature/Filters/FiltersSlice';
import { createMoment } from '../Utilities/DateFormatter';

export type ValidatorName =
  | 'skip'
  | 'required'
  | 'requiredForInput'
  | 'atLeastOneSelection'
  | 'validDate'
  | 'validDateRange'
  | 'maxOneYear'
  | 'maxTwoYear'
  | 'maxThreeYears'
  | 'maxFiveYear';
export type ValidatorFn = (data?: any, state?: FiltersState['value']) => { isValid: boolean; error: string };

export const createRequireValidator = (msg) => {
  return (data: any) => {
    return {
      isValid: !(data === null || data === undefined),
      error: msg,
    };
  };
};
export function getValidatorFn(name: ValidatorName): ValidatorFn {
  switch (name) {
    case 'required':
      return (data: any) => {
        return {
          isValid: !(data === null || data === undefined),
          error: t('This field is required'),
        };
      };
    case 'requiredForInput':
      return (data: string | undefined) => {
        return {
          isValid: !!`${data ?? ''}`.length,
          error: t('This field is required'),
        };
      };
    case 'atLeastOneSelection':
      return (data: any[]) => {
        return {
          isValid: data?.length > 0,
          error: t('Missing selection'),
        };
      };
    case 'validDate':
      return (data: any) => {
        return {
          isValid: createMoment(data).isValid(),
          error: t('Input date is invalid'),
        };
      };
    case 'validDateRange':
      return (data: { From?: any; To?: any }) => {
        if (!data?.From || !data.To) {
          return {
            isValid: false,
            error: t('Input date range is invalid'),
          };
        }
        const from = createMoment(data?.From);
        const to = createMoment(data?.To);
        const limitation = createMoment('1900-1-1');
        if (!from.isValid() || !to.isValid() || !from.isSameOrAfter(limitation)) {
          return {
            isValid: false,
            error: t('Input date range is invalid'),
          };
        }
        return {
          isValid: from.isSameOrBefore(to),
          error: t('Start date must be before end date'),
        };
      };
    case 'maxOneYear':
      return (data: { From?: any; To?: any }) => {
        return {
          isValid:
            createMoment(data?.From).isValid() &&
            createMoment(data?.To).isValid() &&
            createMoment(data?.From).add(1, 'y').isAfter(createMoment(data?.To)),
          error: t('Date range should be less than 1 year'),
        };
      };

    case 'maxTwoYear':
      return (data: { From?: any; To?: any }) => {
        return {
          isValid:
            createMoment(data?.From).isValid() &&
            createMoment(data?.To).isValid() &&
            createMoment(data?.From).add(2, 'y').isAfter(createMoment(data?.To)),
          error: t('Date range should be less than 2 years'),
        };
      };
    case 'maxThreeYears':
      return (data: { From?: any; To?: any }) => {
        return {
          isValid:
            createMoment(data?.From).isValid() &&
            createMoment(data?.To).isValid() &&
            createMoment(data?.From).add(3, 'y').isAfter(createMoment(data?.To)),
          error: t('Date range should be less than 3 years'),
        };
      };
    case 'maxFiveYear':
      return (data: { From?: any; To?: any }) => {
        return {
          isValid:
            createMoment(data?.From).isValid() &&
            createMoment(data?.To).isValid() &&
            createMoment(data?.From).add(5, 'y').isAfter(createMoment(data?.To)),
          error: t('Date range should be less than 5 years'),
        };
      };
    case 'skip':
    default:
      return (data?: any) => ({
        isValid: true,
        error: '',
      });
  }
}
